import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { RouteConfig } from '@app/routing/RenderRoutes'

import { Navigate } from '@lib/react-router-dom'

import { PasswordChangePage } from '@pages/Profile/AccountSettings/SecuritySettings/ChangePassword/PasswordChange.page'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useProductReadContext } from '@context/ProductContext'
import { useWalletsReadContext } from '@context/UserWalletContext'

import { getAccountActionAccessLocks } from '@utils/AccountAccess/accountAccessCheck'
import { getAccountAccessStatuses } from '@utils/AccountAccess/accountAccessStatuses'
import {
  hasProductPendingDocumentVerificationStatus,
  isAccountDocumentAllowedUpload,
  isAccountPersonalInfoDocumentAllowedUpload,
} from '@utils/AccountAccess/accountDocumentStatuses'
import {
  allowKYCUpdate,
  isAccountKycUpdateDetailsRequired,
  isDepositBeforeKYC,
  isDepositPlaced,
  isSumsubNeeded,
  isSumsubSupported,
} from '@utils/AccountAccess/accountKYCStatuses'
import {
  hasClearedAppropriatenessTest,
  hasProductActivatedStatus,
  hasProductAppropriatenessTestSubmittedStatus,
  isAppropriatenessTestMaximumRetakeLimitReached,
  isEconomicProfileTestNeeded,
  isProductAppropriatenessInvalid,
  isProductRegistered,
  showApptestRiskScreen,
} from '@utils/AccountAccess/accountProductStatuses'
import { isTickmillUK } from '@utils/companyName.utils'
import { useNotificationFunctionality } from '@utils/notifications'

import { hasShouldVerifyPhoneNumber } from '@model/AccountDetailedDto'
import {
  AccountDetailedStatus,
  isAccountAppropriatenessTestSubmittedStatus,
  isAccountPendingDocumentVerificationStatus,
} from '@model/AccountDetailedStatus'
import { DocumentCategoryLinkMap, DocumentCategoryType } from '@model/DocumentCategories'
import { isTickmillProductTypeETD } from '@model/TickmillProductType'

import { useDocumentVerificationPath } from '@hooks/useDocumentVerificationPath'

import { useIBRoutesConditions } from '../IntroducingBroker/IntroducingBrokerRoutes'
import { IBSignUpPage } from '../IntroducingBroker/SignUp/IBSignUpPage'
import { IBQuestionnairePage } from '../IntroducingBroker/SignUp/Questionnaire/IBQuestionnairePage'
import { IBSignupThankYouPage } from '../IntroducingBroker/SignUp/ThankYou/IBSignupThankYouPage'
import { IBSignUpTypePage } from '../IntroducingBroker/SignUp/Type/IBSignUpTypePage'
import { AccountSettingsPage } from './AccountSettings/AccountSettingsPage'
import { SecuritySettingsPage } from './AccountSettings/SecuritySettings/SecuritySettings.page'
import { TwoFactorAuthPage } from './AccountSettings/SecuritySettings/TwoFactor/TwoFactorAuth.page'
import { BankAccountsPage } from './BankAccounts/BankAccountsPage'
import { CreateBankAccountPage } from './BankAccounts/CreateBankAccountPage'
import { ClientClassificationPage } from './ClientClassification/ClientClassificationPage'
import { DocumentManagementPage } from './DocumentManagement/DocumentManagementPage'
import { DocumentManagementAdditionalDocumentsPage } from './DocumentManagementUpload/DocumentManagementAdditionalDocumentsPage'
import { DocumentManagementCompanyPage } from './DocumentManagementUpload/DocumentManagementCompany.page'
import { DocumentManagementIdentificationPage } from './DocumentManagementUpload/DocumentManagementIdentification.page'
import { DocumentManagementPaymentsPage } from './DocumentManagementUpload/DocumentManagementPayments.page'
import { DocumentManagementProofOfAddressPage } from './DocumentManagementUpload/DocumentManagementProofOfAddress.page'
import { DocumentVerificationPage } from './DocumentVerification/DocumentVerificationPage'
import { FirstTimeDepositPage } from './FirstTimeDeposit/FirstTimeDepositPage'
import { IntroducingBrokerPage } from './IntroducingBroker/IntroducingBrokerPage'
import { MarketDataClassificationPage } from './MarketDataClassification/MarketDataClassificationPage'
import { NotificationCenter, NotificationType } from './Notifications/NotificationCenter'
import { NotificationDetailsPage } from './Notifications/NotificationDetailsPage'
import { NotificationsPage } from './Notifications/Notifications.page'
import { PersonalInfoPage } from './PersonalInfo/PersonalInfoPage'
import { PhoneNumbersPage } from './PhoneNumbers/PhoneNumbersPage'
import { Profile } from './Profile'
import { SumsubProfilePage } from './Sumsub/SumsubProfilePage'
import { W8BeneficialEPage } from './W8Beneficial/W8BeneficialEPage'
import { W8BeneficialPage } from './W8Beneficial/W8BeneficialPage'
import { YearlyKycUpdateDetailsPage } from './YearlyKycUpdateDetails/YearlyKycUpdateDetailsPage'
import { YearlyKycUpdateDetailsSuccessPage } from './YearlyKycUpdateDetails/YearlyKycUpdateSuccessPage'

export const useProfileRoutes = (): RouteConfig[] => {
  const visible = useProfileRoutesConditions()
  const { account } = useAccountReadContext()
  const { isAccountTradersRoomActionAccessLocked } = getAccountActionAccessLocks(account)
  const { isIBVisible, isSignupVisible } = useIBRoutesConditions()
  const docVerificationPath = useDocumentVerificationPath()
  const { enableNotifications } = useNotificationFunctionality()
  const { t } = useTranslation()

  return useMemo(
    () => [
      {
        path: 'profile',
        outlet: <Profile />,
        condition: visible.isOnlyKYCUpdateVisible,
        meta: { breadcrumbLabel: t('Profile.Profile') },
        children: [
          {
            path: 'kyc-update-details/success',
            element: <YearlyKycUpdateDetailsSuccessPage />,
          },
          {
            path: 'kyc-update-details',
            element: <YearlyKycUpdateDetailsPage />,
            condition: allowKYCUpdate(account),
          },
          {
            path: 'logout',
            element: <Navigate to={'/logout'} />,
          },
        ],
      },
      {
        path: 'profile',
        outlet: <Profile />,
        condition: !visible.isOnlyKYCUpdateVisible,
        meta: { breadcrumbLabel: t('Profile.Profile') },
        children: [
          {
            path: 'notifications',
            outlet: <NotificationsPage />,
            element: <Navigate to={'./all'} />,
            condition: enableNotifications,
            meta: { breadcrumbLabel: t('Notifications.Notifications') },
            children: [
              {
                path: 'all',
                element: <NotificationCenter type={NotificationType.ALL} />,
              },
              {
                path: 'general',
                element: <NotificationCenter type={NotificationType.GENERAL} />,
              },
              {
                path: 'platform',
                element: <NotificationCenter type={NotificationType.PLATFORM} />,
              },
              {
                path: 'promotional',
                element: <NotificationCenter type={NotificationType.PROMOTIONAL} />,
              },
            ],
          },
          {
            path: 'notifications/:notificationId/:clientNotificationId?',
            element: <NotificationDetailsPage />,
          },
          {
            path: 'personal-info',
            element: <PersonalInfoPage />,
            meta: { breadcrumbLabel: t('Profile.Personal Details') },
          },
          {
            path: 'document-verification',
            element: <DocumentVerificationPage />,
          },
          {
            path: 'document-management',
            element: <DocumentManagementPage />,
            condition: visible.isDocManagementVisible,
            meta: { breadcrumbLabel: t('Profile.Documents') },
            children: [
              {
                path: DocumentCategoryLinkMap[DocumentCategoryType.Personal],
                element: <DocumentManagementIdentificationPage />,
              },
              {
                path: DocumentCategoryLinkMap[DocumentCategoryType.Address],
                element: <DocumentManagementProofOfAddressPage />,
              },
              {
                path: DocumentCategoryLinkMap[DocumentCategoryType.Corporate],
                element: <DocumentManagementCompanyPage />,
              },
              {
                path: DocumentCategoryLinkMap[DocumentCategoryType.Additional],
                element: <DocumentManagementAdditionalDocumentsPage />,
              },
              {
                path: DocumentCategoryLinkMap[DocumentCategoryType.Payment],
                element: <DocumentManagementPaymentsPage />,
              },
            ],
          },
          {
            path: 'verification',
            element: <SumsubProfilePage />,
            condition: visible.isSumsubVisible,
            meta: { breadcrumbLabel: t('Verification') },
          },
          {
            path: 'account-settings',
            element: <AccountSettingsPage />,
            meta: { breadcrumbLabel: t('Profile.Preferences') },
          },
          {
            path: 'security-settings',
            element: <Navigate to={'./password-change'} />,
            outlet: <SecuritySettingsPage />,
            meta: { breadcrumbLabel: t('Profile.Security Settings') },
            children: [
              {
                path: 'password-change',
                element: <PasswordChangePage />,
              },
              {
                path: '2fa',
                element: <TwoFactorAuthPage />,
              },
            ],
          },
          {
            path: 'phone-numbers',
            element: <PhoneNumbersPage />,
            meta: { breadcrumbLabel: t('Profile.Phone Numbers') },
          },
          {
            path: 'logout',
            element: <Navigate to={'/logout'} />,
          },
          {
            path: 'first-time-deposit',
            element: <FirstTimeDepositPage />,
            condition: visible.isFirstTimeDepositVisible,
            meta: { breadcrumbLabel: t('Deposit') },
          },
          {
            path: 'client-classification',
            element: <ClientClassificationPage />,
            condition: visible.isClientClassificationVisible,
            meta: { breadcrumbLabel: t('Profile.Client Classification') },
          },
          {
            path: 'tax-form',
            element: <W8BeneficialPage />,
            condition: visible.isTaxFormVisible,
            locked: isAccountTradersRoomActionAccessLocked,
            meta: { breadcrumbLabel: t('Profile.tax form') },
          },
          {
            path: 'e-tax-form',
            element: <W8BeneficialEPage />,
            condition: visible.isETaxFormVisible,
            locked: isAccountTradersRoomActionAccessLocked,
            meta: { breadcrumbLabel: t('Profile.W-8BEN-e Form') },
          },
          {
            path: 'introducing-broker',
            element: <IntroducingBrokerPage />,
            condition: isIBVisible && !isSignupVisible,
            children: [
              {
                path: 'register',
                outlet: <IBSignUpPage />,
                children: [
                  {
                    path: ':subCategoryId',
                    children: [
                      {
                        path: 'questionnaire',
                        element: <IBQuestionnairePage />,
                      },
                      {
                        path: 'thank-you',
                        element: <IBSignupThankYouPage />,
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: 'introducing-broker',
            condition: isIBVisible && isSignupVisible,
            element: <Navigate to='/profile/introducing-broker/register/type' />,
            children: [
              {
                path: 'register',
                outlet: <IBSignUpPage />,
                children: [
                  {
                    path: 'type',
                    element: <IBSignUpTypePage isProfile />,
                  },
                  {
                    path: ':subCategoryId',
                    children: [
                      {
                        path: 'questionnaire',
                        element: <IBQuestionnairePage />,
                      },
                      {
                        path: 'thank-you',
                        element: <IBSignupThankYouPage />,
                      },
                    ],
                  },
                  {
                    path: '*',
                    element: <Navigate to='/profile/introducing-broker/register/type' />,
                  },
                ],
              },
              {
                path: '*',
                element: <Navigate to='/profile/introducing-broker/register/type' />,
              },
            ],
          },
          {
            path: 'bank-accounts',
            element: <BankAccountsPage />,
            condition: visible.isBankAccountVisible,
            locked: isAccountTradersRoomActionAccessLocked,
            meta: { breadcrumbLabel: t('Profile.Bank Accounts') },
            children: [
              {
                path: 'new',
                element: <CreateBankAccountPage />,
              },
            ],
          },
          {
            path: 'market-data-classification',
            element: <MarketDataClassificationPage />,
            condition: visible.isMarketDataClassificationVisible,
            meta: { breadcrumbLabel: t('Profile.Market Data Classification') },
          },
          {
            path: 'kyc-update-details/success',
            element: <YearlyKycUpdateDetailsSuccessPage />,
          },
          {
            path: 'kyc-update-details',
            element: <YearlyKycUpdateDetailsPage />,
            condition: allowKYCUpdate(account),
          },
          {
            path: 'kyc-update-details',
            element: <Navigate to='/profile/personal-info' />,
            condition: !allowKYCUpdate(account),
          },
          {
            path: '*',
            element: <Navigate to={docVerificationPath} />,
            condition: !visible.isLoading,
          },
        ],
      },
    ],
    [
      visible.isOnlyKYCUpdateVisible,
      visible.isDocManagementVisible,
      visible.isSumsubVisible,
      visible.isFirstTimeDepositVisible,
      visible.isClientClassificationVisible,
      visible.isTaxFormVisible,
      visible.isETaxFormVisible,
      visible.isBankAccountVisible,
      visible.isMarketDataClassificationVisible,
      visible.isLoading,
      t,
      account,
      enableNotifications,
      isAccountTradersRoomActionAccessLocked,
      isIBVisible,
      isSignupVisible,
      docVerificationPath,
    ]
  )
}

export interface ProfileRoutesConditions {
  isClientClassificationVisible: boolean
  isTaxFormVisible: boolean
  isETaxFormVisible: boolean
  isAppropriatenessTestVisible: boolean
  isEconomicProfileVisible: boolean
  isBankAccountVisible: boolean
  isMarketDataClassificationVisible: boolean
  isFirstTimeDepositVisible: boolean
  isDocManagementVisible: boolean
  isOnlyKYCUpdateVisible: boolean
  isSumsubVisible: boolean
  isAppropriatenessTestRetakeAllowed: boolean
  isUKProductAppropriatenessMenuVisible: boolean
  isLoading?: boolean
}

export const useProfileRoutesConditions = (): ProfileRoutesConditions => {
  const { account } = useAccountReadContext()
  const { product } = useProductReadContext()
  const { isDefaultCFDProductType } = useProductReadContext()

  const { wallets = [] } = useWalletsReadContext()

  const { isAccountActivatedStatus } = getAccountAccessStatuses(account, product)

  const isClientClassificationVisible = useMemo(() => {
    return !!account?.visibilityConfiguration.hasClassification
  }, [account])

  const isTaxFormVisible = useMemo(() => {
    return !!account?.visibilityConfiguration.hasStocks && isDefaultCFDProductType()
  }, [account?.visibilityConfiguration.hasStocks, isDefaultCFDProductType])

  const isETaxFormVisible = useMemo(() => {
    return !!account?.visibilityConfiguration.hasCorporateStocks && isDefaultCFDProductType()
  }, [account?.visibilityConfiguration.hasCorporateStocks, isDefaultCFDProductType])

  const isAppropriatenessTestVisible = isProductAppropriatenessInvalid(product, account)
  const isEconomicProfileVisible =
    isEconomicProfileTestNeeded(account, product) && !isAppropriatenessTestVisible

  const isBankAccountVisible = useMemo(() => {
    return isAccountActivatedStatus
  }, [isAccountActivatedStatus])

  const isOnlyKYCUpdateVisible = useMemo(
    () =>
      !isAppropriatenessTestVisible &&
      !isEconomicProfileVisible &&
      !showApptestRiskScreen(account, product) &&
      isAccountKycUpdateDetailsRequired(account, product),
    [account, product, isAppropriatenessTestVisible, isEconomicProfileVisible]
  )

  const isAppropriatenessTestRetakeAllowed = useMemo(() => {
    return (
      hasProductAppropriatenessTestSubmittedStatus(account, product) &&
      !hasClearedAppropriatenessTest(account, product) &&
      !isAppropriatenessTestMaximumRetakeLimitReached(account, product)
    )
  }, [account, product])

  const isUKProductAppropriatenessMenuVisible = useMemo(() => {
    if (!isTickmillUK(account)) {
      return false
    }

    if (!product || !isProductRegistered(account, product)) {
      return false
    }

    const isActivatedAccount = account?.status?.id === AccountDetailedStatus.Activated
    const isCurrentProductAppTestSubmitted = hasProductAppropriatenessTestSubmittedStatus(
      account,
      product
    )
    const hasNotPassedCurrentProduct = !hasClearedAppropriatenessTest(account, product)
    const hasCurrentProductReachedRetakeLimit = isAppropriatenessTestMaximumRetakeLimitReached(
      account,
      product
    )

    if (isActivatedAccount) {
      return hasNotPassedCurrentProduct
    }

    if (isCurrentProductAppTestSubmitted) {
      return isAppropriatenessTestRetakeAllowed || hasCurrentProductReachedRetakeLimit
    }

    return false
  }, [account, product, isAppropriatenessTestRetakeAllowed])

  const isMarketDataClassificationVisible = useMemo(() => {
    if (!account?.marketDataClassification) {
      return false
    }
    if (!isTickmillUK(account)) {
      return false
    }
    if (!hasProductActivatedStatus(account, product)) {
      return false
    }
    return isTickmillProductTypeETD(product)
  }, [account, product])

  const isFirstTimeDepositVisible = useMemo(() => {
    if (!isDepositBeforeKYC(account) || !wallets?.length) {
      return false
    }
    const isAppTestSubmitted = isAccountAppropriatenessTestSubmittedStatus(account?.status?.id)
    const isPendingDocumentVerification = isAccountPendingDocumentVerificationStatus(
      account?.status?.id
    )
    const noDepositPlaced = !isDepositPlaced(account)
    if (isAppTestSubmitted && noDepositPlaced) {
      return true
    }
    return isPendingDocumentVerification && noDepositPlaced
  }, [account, wallets])

  const isDocManagementVisible = useMemo(() => {
    if (isSumsubNeeded(account)) {
      return false
    }

    if (isEconomicProfileVisible || showApptestRiskScreen(account, product)) return false

    if (
      hasProductAppropriatenessTestSubmittedStatus(account, product) &&
      hasClearedAppropriatenessTest(account, product)
    ) {
      return true
    }
    if (hasProductPendingDocumentVerificationStatus(account, product)) {
      return true
    }
    return isAccountActivatedStatus
  }, [account, isAccountActivatedStatus, isEconomicProfileVisible, product])

  const isSumsubVisible = useMemo(() => {
    if (
      (hasProductAppropriatenessTestSubmittedStatus(account, product) &&
        hasClearedAppropriatenessTest(account, product)) ||
      hasProductPendingDocumentVerificationStatus(account, product) ||
      (isAccountActivatedStatus && isAccountPersonalInfoDocumentAllowedUpload(account))
    ) {
      return isSumsubSupported(account)
    }

    return false
  }, [account, isAccountActivatedStatus, product])

  return {
    isClientClassificationVisible,
    isTaxFormVisible,
    isETaxFormVisible,
    isAppropriatenessTestVisible,
    isEconomicProfileVisible,
    isBankAccountVisible,
    isMarketDataClassificationVisible,
    isFirstTimeDepositVisible,
    isDocManagementVisible,
    isOnlyKYCUpdateVisible,
    isSumsubVisible,
    isAppropriatenessTestRetakeAllowed,
    isUKProductAppropriatenessMenuVisible,
    isLoading: !account,
  }
}

interface ProfileRoutesRequired {
  appropriatenessTest: boolean
  documentManagement: boolean
  shouldVerifyPhoneNumber: boolean
}

export const useProfileRoutesRequired = (): ProfileRoutesRequired => {
  const { product } = useProductReadContext()
  const { account } = useAccountReadContext()
  const appropriatenessTest = isProductAppropriatenessInvalid(product, account)

  const documentManagement = useMemo(() => isAccountDocumentAllowedUpload(account), [account])

  const shouldVerifyPhoneNumber = useMemo(() => hasShouldVerifyPhoneNumber(account), [account])

  return {
    appropriatenessTest,
    documentManagement,
    shouldVerifyPhoneNumber,
  }
}
